import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Toaster as Sonner } from 'sonner';

type ToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * Wrap sonner's `<Toaster>` with the app's theme + neutral styling.
 *
 * Stacking-context escape hatch: sonner's viewport lives inside its
 * parent React tree. When ANY ancestor of the parent creates a
 * stacking context (`transform`, `will-change`, `filter`, `contain`,
 * `isolation` in some tree — even indirect via a Tailwind utility
 * on the layout shell), the fixed-position toast is trapped inside
 * that context and cannot paint over Radix Dialog / AlertDialog
 * portals that mount as direct children of `document.body`. Every
 * z-index bump is then a no-op because the browser resolves stacking
 * across contexts before z-index.
 *
 * Fix: portal the whole Toaster to a dedicated `<div>` appended to
 * `document.body`. That guarantees the toast lives at the same DOM
 * level as every Radix portal, so the elevated `--sonner-z-index`
 * below is the ONLY thing that decides painting order — and it wins
 * against Radix's `z-50` unconditionally.
 */
const FORCE_Z = 2_147_483_000;

const useSonnerHostNode = (): HTMLElement | null => {
  const [node, setNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const host = document.createElement('div');
    host.setAttribute('data-sonner-host', 'true');
    host.style.position = 'fixed';
    host.style.inset = '0';
    host.style.pointerEvents = 'none';
    host.style.zIndex = String(FORCE_Z);
    document.body.appendChild(host);
    setNode(host);
    return () => {
      document.body.removeChild(host);
    };
  }, []);
  return node;
};

const Toaster = (props: ToasterProps) => {
  const { theme = 'system' } = useTheme();
  const consumerStyle = (props.style ?? {}) as React.CSSProperties;
  const consumerToastOptions = props.toastOptions ?? {};
  const consumerToastStyle =
    (consumerToastOptions.style as React.CSSProperties | undefined) ?? {};
  const host = useSonnerHostNode();
  if (!host) return null;

  return createPortal(
    <Sonner
      theme={theme as ToasterProps['theme']}
      className="toaster group"
      {...props}
      style={{
        ...consumerStyle,
        zIndex: FORCE_Z,
        ['--sonner-z-index' as string]: String(FORCE_Z),
      }}
      toastOptions={{
        ...consumerToastOptions,
        classNames: {
          toast:
            'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg',
          description: 'group-[.toast]:text-muted-foreground',
          actionButton:
            'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
          cancelButton:
            'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
          ...(consumerToastOptions.classNames ?? {}),
        },
        style: {
          ...consumerToastStyle,
          zIndex: FORCE_Z,
          pointerEvents: 'auto',
        },
      }}
    />,
    host
  );
};

export { Toaster };
